<template>
  <div
    v-if="store.state.indexedDocumentErrors[document?.name]?.errors.length"
    class="alert alert-danger"
  >
    <div
      v-for="(errorMessage, index) in store.state.indexedDocumentErrors[
        document?.name
      ]?.errors"
      :key="index"
    >
      {{ errorMessage }}
    </div>
  </div>
  <div class="__hr-form" ref="hrForm"></div>
</template>

<script>
import { reactive, ref, onMounted } from 'vue';
import { Formio } from 'formiojs';
import store from '@/store';
import { DOCUMENT_STATUSES } from '@/modules/definitions';
import { DOCUMENT_NAMES } from '@/core/field-names';
import { formatAddress } from '@/core/address_map';
import t from '@/lib/i18next';
import { useEventListener } from '@/composables/use-event-listener';
import { logWebHrError } from '@/core/logger/log-wrapper';
import { HR_ONBOARDING_STATES } from '../core/onboarding-states';

export default {
  name: 'HrForm',
  props: {
    document: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['update'],
  setup(props, { emit }) {
    const hrForm = ref(null);
    const bodyRight = ref(null);

    const state = reactive({
      selected: null,
      formattedPersonalDetails: {},
    });

    let form = {};

    const FORMS_WITH_PERSONAL_DETAILS = [
      DOCUMENT_NAMES.EMPLOYMENT_ELIGIBILITY_VERIFICATION,
      DOCUMENT_NAMES.FEDERAL_TAX_FORM,
      DOCUMENT_NAMES.STATE_TAX_FORM,
    ];

    function menuToggle() {
      return store.commit('menuToggle');
    }

    function mapPersonalDetails(submissionData) {
      const { address, ...personalDetails } = state.formattedPersonalDetails;

      const updatedPersonalDetails = { ...address, ...personalDetails };

      return Object.keys(updatedPersonalDetails).reduce((data, key) => {
        if (key in submissionData) {
          data[key] = updatedPersonalDetails[key];
        }
        return data;
      }, {});
    }

    async function reSubmitFormsWithPersonalDetails(docToSave) {
      try {
        if (docToSave.name === DOCUMENT_NAMES.PERSONAL_DETAILS) {
          formatPersonalDetails();

          for (const docName of FORMS_WITH_PERSONAL_DETAILS) {
            const docToUpdate = store.state.documents.find(
              (doc) => doc.name === docName
            );

            if (docToUpdate?.submissionId) {
              const mappedPersonalDetails = mapPersonalDetails(
                docToUpdate.formData.data
              );

              docToUpdate.formData.data = {
                ...docToUpdate.formData.data,
                ...mappedPersonalDetails,
              };

              const submissionPayload = {
                documentId: docToUpdate.id,
                submissionId: docToUpdate.submissionId,
                body: {
                  formPath: docToUpdate.formId,
                  requestPayload: {
                    data: docToUpdate.formData.data,
                  },
                },
              };

              await store.dispatch('setSubmission', submissionPayload);
              await store.dispatch('saveForm', docToUpdate);
            }
          }
        }
      } catch (e) {
        logWebHrError({
          message: 'update request error',
          source: 'reSubmitFormsWithPersonalDetails',
          metadata: {
            tabName: docToSave.name,
            documentId: docToSave.id,
            submissionId: docToSave.submissionId,
            formId: docToSave.formId,
            isDataEmpty: !docToSave.formData.data,
          },
          error: e,
        });
      }
    }

    async function submitForm(submittedData) {
      try {
        const documentDetails = {
          documentId: props.document.id,
          submissionId: props.document.submissionId,
          body: {
            formPath: props.document.formId,
            requestPayload: {
              data: submittedData.data,
            },
          },
        };

        const data = await store.dispatch('setSubmission', documentDetails);

        const docToSave = {
          ...props.document,
          submissionId: data._id,
          formData: {
            ...props.document.formData,
            data: data.data,
            submissionId: data._id,
          },
          status: DOCUMENT_STATUSES.DOCUMENT_SUBMITTED,
        };

        await store.dispatch('saveForm', docToSave);

        if (store.getters.onboardingStatus !== HR_ONBOARDING_STATES.STARTED) {
          await store.dispatch(
            'updateOnboardingStatus',
            HR_ONBOARDING_STATES.STARTED
          );
        }

        emit('update');

        store.commit('setIndexedDocumentBackendErrors', {
          errors: [],
          document: props.document,
        });

        reSubmitFormsWithPersonalDetails(docToSave);

        menuToggle();
      } catch (e) {
        logWebHrError({
          message: 'Error during submitting form',
          source: 'hr-form',
          metadata: {
            tabName: props.document.name,
            documentId: props.document.id,
            submissionId: props.document.submissionId,
            formId: props.document.formId,
            hasSubmittingData: !!submittedData.data,
          },
          error: e,
        });

        const errors = [t('hr-form:error-message')];

        store.commit('setIndexedDocumentBackendErrors', {
          errors: errors,
          document: props.document,
        });
      }
    }

    function handleSubmitEvent(data) {
      submitForm(data);
    }

    function registerFormioEventHandlers() {
      form.on('submit', handleSubmitEvent);
    }

    function formatPersonalDetails() {
      const personalDetails = store.state.documents.find(
        (doc) => doc.name === DOCUMENT_NAMES.PERSONAL_DETAILS
      );

      const { data } = personalDetails.formData;

      const formattedAddress = data.address && formatAddress(data.address);

      const city = formattedAddress.locality || formattedAddress.sublocality;

      const address = {
        ...formattedAddress,
        locality: city,
        addressLineWithoutApt: `${formattedAddress.streetNumber} ${formattedAddress.route}`,
        addressLine2: `${city}, ${formattedAddress.administrativeArea} ${formattedAddress.postalCode}`,
      };

      state.formattedPersonalDetails = {
        ...data,
        address,
        middleInitial: data.middleName?.[0] ?? '',
      };
    }

    const delay = (millis) =>
      // eslint-disable-next-line no-unused-vars
      new Promise((resolve, _) => {
        // eslint-disable-next-line no-unused-vars
        setTimeout((_) => resolve(), millis);
      });

    async function initialize() {
      try {
        // Formio does not let you scroll when the form is being rendered
        // TODO: remove this code when formio fix this issue
        await delay(500);

        bodyRight.value = document.querySelector(
          '[data-testid="hr-module-body-right"]'
        );

        const currentForm = store.state.indexedForms[props.document.formId];

        form = await Formio.createForm(hrForm.value, {
          components: currentForm.components,
          saveDraft: true,
          renderMode: 'foo',
          hide: { managerPanel: true },
        });

        form.options.noAlerts = true;

        form.submission = {
          data: props.document.formData.data,
        };

        if (!props.document.submissionId) {
          if (FORMS_WITH_PERSONAL_DETAILS.includes(props.document.name)) {
            formatPersonalDetails();
            const mappedPersonalDetails = mapPersonalDetails(
              form.submission.data
            );

            form.submission = {
              data: {
                ...form.submission.data,
                ...mappedPersonalDetails,
              },
            };
          }
        }

        registerFormioEventHandlers();
      } catch (e) {
        logWebHrError({
          message: 'Failed to initialise Formio form',
          source: 'hr-form',
          metadata: {
            documentId: props.document.id,
            submissionId: props.document.submissionId,
            formId: props.document.formId,
          },
          error: e,
        });
      }
    }

    useEventListener(bodyRight, 'scroll', async function () {
      // TODO: Find a better way to handle this
      const dropdowns = document.getElementsByClassName('pac-container');

      // Why? Because the dropdown position is fixed and it doesn't close when the user scrolls
      for (const dropdown of dropdowns) {
        dropdown.style.display = 'none';
      }
    });

    onMounted(() => {
      initialize();
    });

    return {
      t,
      hrForm,
      state,
      store,
    };
  },
};
</script>

<!-- form.io global styles -->
<!-- please read the guide before editing -->
<!-- https://deputy.atlassian.net/wiki/spaces/EO/pages/3345088565/Form.io+CSS+Framework -->
<style>
.__hr-form h1,
.__hr-form h2,
.__hr-form h3,
.__hr-form h4,
.__hr-form h5,
.__hr-form h6,
.__hr-form label {
  color: var(--co-text-primary);
  font-weight: 600;
  margin-block-start: 0;
}

.__hr-form p,
.__hr-form span {
  color: var(--co-text-secondary);
  font-weight: 400;
}

.__hr-form a {
  color: var(--co-text-highlight);
}

.__hr-form a:hover {
  color: var(--co-text-highlight-hover);
}

.__hr-form a:focus {
  outline: none;
  box-shadow: 0 0 2px 2px var(--co-text-highlight-focus);
  border-radius: 0.5rem;
}

.__hr-form .btn-primary {
  font-weight: 600;
  color: var(--co-text-contrast);
  background-color: var(--co-button-primary-fill);
  transition: all 0.1s ease-in-out;
  margin: 0;
  padding: 13px 23px;
  font-size: 1rem;
  font-family: var(--co-font-primary);
  line-height: 16px;
  border-radius: 10px;
  border: 0;
}

.__hr-form .btn-primary:hover {
  background: var(--co-button-primary-hover);
}

.__hr-form .btn-primary:focus-visible {
  box-shadow: 0 0 0 3px var(--co-input-focus);
}

.__hr-form .btn-primary:focus {
  outline: none;
  background: var(--co-button-primary-hover);
}

.__hr-form .btn-primary[disabled] {
  color: var(--co-text-contrast);
  background: var(--co-button-disabled-primary-fill);
  cursor: not-allowed;
  opacity: 1;
}

.__hr-form .checkbox.form-check {
  margin-top: 0;
}

.__hr-form .radio.form-check label {
  display: block;
  background-color: var(--co-background-secondary);
  padding: 1rem;
  border-radius: 0.5rem;
  border: 2px solid transparent;
}

.__hr-form .radio.form-check label:has(input:focus-visible) {
  box-shadow: 0 0 2px 2px var(--co-accent-primary);
}

.__hr-form .radio.form-check label:has(input:checked) {
  background-color: var(--co-accent-background-secondary);
  border-color: var(--co-radio-border);
}

.__hr-form .radio.form-check input {
  opacity: 0;
}

.__hr-form .radio.form-check span {
  color: var(--co-text-primary);
}

.__hr-form input:not([type='checkbox']) {
  padding: 0 1rem;
  border-radius: 0.5rem;
  background-color: var(--co-background-primary);
  border: 1px solid var(--co-lines-border-solid);
}

.__hr-form input:not([type='checkbox']):hover {
  border-color: var(--co-lines-border-hover);
  cursor: pointer;
}

.__hr-form input:not([type='checkbox']):focus-visible {
  border-color: var(--co-accent-primary);
}

.__hr-form input:not([type='checkbox']):focus {
  outline: none;
  box-shadow: none;
}

.__hr-form input:not([type='checkbox'])[disabled] {
  background-color: var(--co-background-secondary);
  border-color: var(--co-lines-border-solid);
  color: var(--co-text-secondary);
  cursor: not-allowed;
}

.__hr-form input:not([type='checkbox']).is-invalid {
  border-color: var(--co-text-attention);
}

.__hr-form .formio-component-signature canvas {
  border-radius: 0.5rem;
  background-color: var(--co-background-secondary);
  width: 100%;
  aspect-ratio: 4 / 1;
}

.__hr-form .formio-component-signature .signature-pad-refresh {
  background-image: url('data:image/svg+xml,<svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg"><path d="M4.0163 10.2929C2.19768 8.47427 2.19768 5.52571 4.0163 3.7071C5.68885 2.03455 8.31714 1.90022 10.1432 3.30411L9.47181 3.69175C9.23849 3.82645 9.3193 4.23585 9.60019 4.34094L12.468 5.41517C12.6419 5.48023 12.8083 5.38412 12.8389 5.201L13.3425 2.18031C13.3919 1.88451 13.0778 1.60983 12.8445 1.74453L11.9715 2.24853C9.36847 -0.306691 5.18688 -0.291906 2.60208 2.29289C0.00242138 4.89255 0.00242138 9.10744 2.60208 11.7071C5.20175 14.3068 9.41663 14.3068 12.0163 11.7071C12.4068 11.3166 12.4068 10.6834 12.0163 10.2929C11.6258 9.90236 10.9926 9.90236 10.6021 10.2929C8.78347 12.1115 5.83491 12.1115 4.0163 10.2929Z" fill="%233D1CBA"/></svg>');
  background-repeat: no-repeat;
  background-position: center;
  background-color: var(--co-button-secondary-fill);
  border-radius: 0.5rem;
  transition: all 0.1s ease-in-out;
  width: 2rem;
  aspect-ratio: 1;
}

.__hr-form .formio-component-signature .signature-pad-refresh:hover {
  background-color: var(--co-button-secondary-hover);
}

.__hr-form .formio-component-signature .signature-pad-refresh:focus-visible {
  box-shadow: 0 0 0 3px var(--co-input-focus);
}

.__hr-form .formio-component-signature .signature-pad-refresh:focus {
  outline: none;
  box-shadow: none;
}

.__hr-form .formio-component-signature .signature-pad-refresh:active {
  box-shadow: none;
}

.__hr-form .formio-component-signature .signature-pad-footer {
  margin-block-start: 0.75rem;
}

.__hr-form input[type='checkbox'] {
  border-radius: 0.5rem;
  border: 1px solid var(--co-lines-border-solid);
  accent-color: var(--co-accent-primary);
}

.__hr-form input[type='checkbox']:hover {
  border-color: var(--co-lines-border-hover);
  cursor: pointer;
}

.__hr-form input[type='checkbox']:focus-visible {
  border-color: var(--co-accent-primary);
}

.__hr-form input[type='checkbox']:focus {
  outline: none;
  box-shadow: none;
}

.__hr-form .formio-component-select select {
  padding: 0 2rem 0 1rem;
  border-radius: 0.5rem;
  background-color: var(--co-background-primary);
  border: 1px solid var(--co-lines-border-solid);
  appearance: none;
  background-image: url('data:image/svg+xml,<svg width="6" height="4" viewBox="0 0 6 4" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.403716 0H5.59628C5.9555 0 6.13512 0.478061 5.88084 0.758227L3.28556 3.61992C3.12815 3.79336 2.87185 3.79336 2.71444 3.61992L0.119165 0.758227C-0.135116 0.478061 0.044495 0 0.403716 0Z" fill="%231E163C"/></svg>');
  background-repeat: no-repeat;
  background-position: right 13px center;
  text-overflow: ellipsis;
}

.__hr-form .formio-component-select select:hover {
  border-color: var(--co-lines-border-hover);
  cursor: pointer;
}

.__hr-form .formio-component-select select:focus-visible {
  border-color: var(--co-accent-primary);
}

.__hr-form .formio-component-select select:focus {
  outline: none;
  box-shadow: none;
}

.__hr-form .formio-component-select input {
  display: none;
}

.__hr-form
  .formio-component.formio-component-textfield:has(.flatpickr-input)
  input {
  padding: 0 1rem;
  border-radius: 0.5rem;
  background-color: var(--co-background-primary);
  border: 1px solid var(--co-lines-border-solid);
}

.__hr-form
  .formio-component.formio-component-textfield:has(.flatpickr-input)
  input:hover {
  border-color: var(--co-lines-border-hover);
  cursor: pointer;
}

.__hr-form
  .formio-component.formio-component-textfield:has(.flatpickr-input)
  input:focus-visible {
  border-color: var(--co-accent-primary);
}

.__hr-form
  .formio-component.formio-component-textfield:has(.flatpickr-input)
  input:focus {
  outline: none;
  box-shadow: none;
}

.__hr-form .formio-errors .error {
  color: var(--co-text-attention);
}

.__hr-form .alert.alert-danger {
  background-color: var(--co-badge-attention-fill);
  border: 0;
  color: var(--co-text-primary);
}

.__hr-form .alert.alert-danger * {
  color: inherit;
}

.__hr-form .alert.alert-danger ::marker {
  color: var(--co-text-attention);
}

.__hr-form .formio-component:has(+ .formio-component > hr:nth-child(1)),
.__hr-form
  .formio-component:has(hr:nth-last-child(2)):has(:nth-last-child(1):empty) {
  margin-block-end: 0;
}

.__hr-form hr {
  margin-block: 1.5rem;
}

.__hr-form .formio-component {
  margin: 0;
  margin-block-end: 2rem;
}

.__hr-form .formio-component:empty,
.__hr-form .formio-component:not(:has(> :nth-child(1))) {
  display: none;
}

.__hr-form .formio-component > label {
  margin-block: 0 0.6rem;
}

.__hr-form .formio-component-signature .signature-pad-refresh {
  position: absolute;
}

.__hr-form .label-heading .formio-component-htmlelement > h4 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--co-text-primary);
}

.__hr-form .formio-component .label-heading {
  margin-block-end: 0;
}

.__hr-form .description-after-label {
  display: flex;
  flex-direction: column;
}

.__hr-form .description-after-label > label {
  order: -2;
  margin-block-end: 0.25rem;
}

.__hr-form .description-after-label > :nth-child(3) {
  order: -1;
  margin-block-end: 0.6rem;
}

.__hr-form .description-after-label > :nth-child(2) {
  margin-block-end: 0;
}

.__hr-form .description-after-label > :nth-child(2) > :nth-last-child(1) {
  margin-block-end: 0;
}

.__hr-form .mb-0 {
  margin-block-end: 0rem;
}

.__hr-form .mb-1 {
  margin-block-end: 0.25rem;
}

.__hr-form .mb-2 {
  margin-block-end: 0.5rem;
}

.__hr-form .mb-3 {
  margin-block-end: 0.75rem;
}

.__hr-form .mb-4 {
  margin-block-end: 1rem;
}

.__hr-form .mb-5 {
  margin-block-end: 1.25rem;
}

.__hr-form .mb-6 {
  margin-block-end: 1.5rem;
}

.__hr-form .label-text-color-secondary label {
  color: var(--co-text-secondary);
}

.__hr-form .address-autocomplete-container {
  position: relative;
}

.__hr-form
  .address-autocomplete-container
  .address-autocomplete-remove-value-icon {
  cursor: pointer;
  position: absolute;
  margin-top: -7px;
  right: 10px;
  top: 50%;
}

.flatpickr-calendar.open[class*='arrow'] {
  display: none;
}

.flatpickr-calendar[class*='arrow'] {
  width: auto;
  padding: 1rem;
}

.flatpickr-calendar[class*='arrow'] .flatpickr-months {
  align-items: center;
  margin-block-end: 1rem;
}

.flatpickr-calendar[class*='arrow'] .flatpickr-prev-month,
.flatpickr-calendar[class*='arrow'] .flatpickr-next-month {
  position: static;
}

.flatpickr-calendar[class*='arrow'] .flatpickr-prev-month:hover,
.flatpickr-calendar[class*='arrow'] .flatpickr-next-month:hover svg {
  fill: var(--co-accent-primary);
}

.flatpickr-calendar[class*='arrow'] .flatpickr-current-month {
  display: flex;
  justify-content: space-evenly;
  position: static;
  width: auto;
}

.flatpickr-calendar:before,
.flatpickr-calendar:after {
  display: none !important;
}
</style>
